<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0 d-flex justify-content-between align-items-center">
            <div>
              <h5 class="mb-0">用户管理</h5>
              <p class="mb-0 text-sm text-muted">管理系统中的所有用户</p>
            </div>
            <button class="btn btn-primary btn-sm mb-0" @click="openAdd">+ 新增用户</button>
          </div>

          <div class="table-responsive">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">ID</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">用户名</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">角色</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">状态</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">创建时间</th>
                  <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="6" class="text-center py-4 text-sm text-muted">加载中...</td>
                </tr>
                <tr v-else-if="users.length === 0">
                  <td colspan="6" class="text-center py-4 text-sm text-muted">暂无数据</td>
                </tr>
                <tr v-for="u in users" :key="u.id">
                  <td><p class="text-sm font-weight-bold mb-0">{{ u.id }}</p></td>
                  <td><p class="text-sm font-weight-bold mb-0">{{ u.username }}</p></td>
                  <td><p class="text-sm mb-0">{{ u.role_name }}</p></td>
                  <td>
                    <span class="badge badge-sm" :class="u.status === 1 ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                      {{ u.status === 1 ? '启用' : '禁用' }}
                    </span>
                  </td>
                  <td><p class="text-sm mb-0">{{ u.created_at }}</p></td>
                  <td>
                    <button class="btn btn-link btn-sm mb-0 p-0 me-2 text-info" @click="openEdit(u)">编辑</button>
                    <button class="btn btn-link btn-sm mb-0 p-0 me-2 text-primary" @click="openAPIKey(u)">API Key</button>
                    <button class="btn btn-link btn-sm mb-0 p-0 text-danger" :disabled="u.id === 1" @click="confirmDelete(u)">删除</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-box card p-4" style="width:420px">
        <h6 class="mb-3">{{ isEdit ? '编辑用户' : '新增用户' }}</h6>
        <div v-if="formError" class="alert alert-danger text-white text-sm py-2 mb-3">{{ formError }}</div>
        <div class="mb-3">
          <label class="form-label text-sm">用户名</label>
          <input v-model="form.username" type="text" class="form-control" :disabled="isEdit" placeholder="请输入用户名" />
        </div>
        <div class="mb-3">
          <label class="form-label text-sm">密码{{ isEdit ? '（不填则不修改）' : '' }}</label>
          <input v-model="form.password" type="password" class="form-control" placeholder="请输入密码" />
        </div>
        <div class="mb-3">
          <label class="form-label text-sm">角色</label>
          <select v-model="form.role_id" class="form-control">
            <option v-for="r in roles" :key="r.id" :value="r.id">{{ r.name }}</option>
          </select>
        </div>
        <div class="mb-4">
          <label class="form-label text-sm">状态</label>
          <select v-model="form.status" class="form-control">
            <option :value="1">启用</option>
            <option :value="0">禁用</option>
          </select>
        </div>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-sm btn-secondary mb-0" @click="closeModal">取消</button>
          <button class="btn btn-sm btn-primary mb-0" :disabled="saving" @click="saveUser">
            {{ saving ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 删除确认弹窗 -->
    <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
      <div class="modal-box card p-4" style="width:360px">
        <h6 class="mb-2">确认删除</h6>
        <p class="text-sm mb-4">确定要删除用户 <strong>{{ deleteTarget.username }}</strong> 吗？此操作不可恢复。</p>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-sm btn-secondary mb-0" @click="deleteTarget = null">取消</button>
          <button class="btn btn-sm btn-danger mb-0" :disabled="saving" @click="doDelete">确认删除</button>
        </div>
      </div>
    </div>

    <!-- API Key 管理弹窗 -->
    <div v-if="apiKeyTarget" class="modal-overlay" @click.self="apiKeyTarget = null">
      <div class="modal-box card p-4" style="width:480px">
        <h6 class="mb-2">{{ apiKeyTarget.username }} 的 API Key</h6>
        <p class="text-sm text-muted mb-3">
          用于该账号通过接口 <code>/api/open/revenue/detail</code> 拉取详细数据，请求头携带
          <code>X-API-Key</code>。
        </p>
        <div v-if="apiKeyLoading" class="text-sm text-muted py-2">加载中...</div>
        <template v-else>
          <div v-if="apiKeyValue" class="mb-3">
            <label class="form-label text-sm">当前 Key</label>
            <div class="input-group">
              <input type="text" class="form-control form-control-sm" :value="apiKeyValue" readonly />
              <button class="btn btn-sm btn-outline-secondary mb-0" @click="copyAPIKey">复制</button>
            </div>
            <p v-if="copied" class="text-success text-xs mt-1 mb-0">已复制到剪贴板</p>
          </div>
          <p v-else class="text-sm text-muted mb-3">该账号尚未生成 API Key</p>
        </template>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-sm btn-secondary mb-0" @click="apiKeyTarget = null">关闭</button>
          <button v-if="apiKeyValue" class="btn btn-sm btn-outline-danger mb-0" :disabled="apiKeySaving" @click="revokeAPIKey">撤销</button>
          <button class="btn btn-sm btn-primary mb-0" :disabled="apiKeySaving" @click="generateAPIKey">
            {{ apiKeyValue ? '重新生成' : '生成 Key' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { request } from "@/api/index.js";

export default {
  name: "UserManage",
  data() {
    return {
      loading: true,
      saving: false,
      users: [],
      roles: [],
      showModal: false,
      isEdit: false,
      form: { id: null, username: "", password: "", role_id: 1, status: 1 },
      formError: "",
      deleteTarget: null,
      apiKeyTarget: null,
      apiKeyValue: "",
      apiKeyLoading: false,
      apiKeySaving: false,
      copied: false,
    };
  },
  async created() {
    await Promise.all([this.loadUsers(), this.loadRoles()]);
  },
  methods: {
    async loadUsers() {
      try {
        const data = await request("/api/users");
        this.users = (data || []).sort((a, b) => a.id - b.id);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async loadRoles() {
      try {
        const data = await request("/api/roles");
        this.roles = data || [];
      } catch (e) {
        console.error(e);
      }
    },
    openAdd() {
      this.isEdit = false;
      this.form = { id: null, username: "", password: "", role_id: this.roles[0]?.id || 1, status: 1 };
      this.formError = "";
      this.showModal = true;
    },
    openEdit(u) {
      this.isEdit = true;
      this.form = { id: u.id, username: u.username, password: "", role_id: u.role_id, status: u.status };
      this.formError = "";
      this.showModal = true;
    },
    closeModal() { this.showModal = false; },
    async saveUser() {
      this.formError = "";
      if (!this.isEdit && !this.form.username) { this.formError = "请填写用户名"; return; }
      if (!this.isEdit && !this.form.password) { this.formError = "请填写密码"; return; }
      this.saving = true;
      try {
        if (this.isEdit) {
          await request(`/api/users/${this.form.id}`, {
            method: "PUT",
            body: JSON.stringify({ password: this.form.password, role_id: this.form.role_id, status: this.form.status }),
          });
        } else {
          await request("/api/users", {
            method: "POST",
            body: JSON.stringify({ username: this.form.username, password: this.form.password, role_id: this.form.role_id, status: this.form.status }),
          });
        }
        this.showModal = false;
        await this.loadUsers();
      } catch (e) {
        this.formError = e.message;
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(u) { this.deleteTarget = u; },
    async doDelete() {
      this.saving = true;
      try {
        await request(`/api/users/${this.deleteTarget.id}`, { method: "DELETE" });
        this.deleteTarget = null;
        await this.loadUsers();
      } catch (e) {
        alert(e.message);
      } finally {
        this.saving = false;
      }
    },
    async openAPIKey(u) {
      this.apiKeyTarget = u;
      this.apiKeyValue = "";
      this.copied = false;
      this.apiKeyLoading = true;
      try {
        const data = await request(`/api/users/${u.id}/apikey`);
        this.apiKeyValue = data?.api_key || "";
      } catch (e) {
        alert(e.message);
      } finally {
        this.apiKeyLoading = false;
      }
    },
    async generateAPIKey() {
      this.apiKeySaving = true;
      this.copied = false;
      try {
        const data = await request(`/api/users/${this.apiKeyTarget.id}/apikey`, { method: "POST" });
        this.apiKeyValue = data?.api_key || "";
        await this.loadUsers();
      } catch (e) {
        alert(e.message);
      } finally {
        this.apiKeySaving = false;
      }
    },
    async revokeAPIKey() {
      if (!confirm("确定要撤销该 API Key 吗？")) return;
      this.apiKeySaving = true;
      try {
        await request(`/api/users/${this.apiKeyTarget.id}/apikey`, { method: "DELETE" });
        this.apiKeyValue = "";
        this.copied = false;
        await this.loadUsers();
      } catch (e) {
        alert(e.message);
      } finally {
        this.apiKeySaving = false;
      }
    },
    async copyAPIKey() {
      try {
        await navigator.clipboard.writeText(this.apiKeyValue);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      } catch {
        // 忽略剪贴板权限错误
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
}
.modal-box { border-radius: 12px; }
</style>
